import React, { useMemo, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import Radar from '../beam/Radar';
import DeviceRow from '../beam/DeviceRow';
import DropZone from '../beam/DropZone';
import QueueRail from '../beam/QueueRail';
import TransportToggle from '../beam/TransportToggle';
import { useNearbyPeers } from '../../hooks/useNearbyPeers';
import { useMesh } from '../../hooks/useMesh';
import useToast from '../../components/toast/useToast';
import { useFileDrop } from '../../hooks/useFileDrop';
import { useCloudFileShare } from '../../hooks/useCloudFileShare';
import './DesktopBeam.css';

/**
 * F10.1 + F11.3 — Desktop Beam workspace (artboard 19).
 *
 * Two-column layout: radar + device list on the left, transfer queue rail on
 * the right. Mounted as a full-screen overlay above the chat region, triggered
 * by the "Beam" entry in DesktopSidebar (or the mobile MobileNav radar button
 * on small viewports).
 *
 * F11.3 wires the drag-drop pipeline:
 *   - `useFileDrop` attaches to `.dbeam__main` so the dropzone activates when
 *     a file is dragged anywhere over the workspace.
 *   - The DropZone overlay is shown only when BOTH a peer is selected AND a
 *     drag is in progress — dropping with no peer selected surfaces a toast
 *     rather than silently dropping the files into the void.
 *   - Each dropped file fans out to `mesh.startSend(file, peerId)`; the mesh
 *     handles concurrency caps and the 2 GB LAN size cap.
 *
 * Props:
 *  - `onClose`     — optional, fires when the user dismisses the workspace
 *                    (close button + Esc).
 *  - `onSelectPeer`— forwarded from both the Radar and DeviceRow lists.
 */
const DesktopBeam = ({ onClose, onSelectPeer }) => {
  const { peers, scanning } = useNearbyPeers();
  const [selectedId, setSelectedId] = useState(null);
  const mesh = useMesh();
  const { showToast } = useToast();

  // LAN ↔ Cloud transport pick for this Beam session. LAN = direct WebRTC to a
  // device; Cloud = upload to Supabase Storage, sent to the whole room (S8).
  // The Cloud option only exists when the provider supports it (Supabase) —
  // see the toggle render below; on Firebase we stay LAN-only.
  const [transport, setTransport] = useState('lan');

  // S8 — internet file share. `enabled` is the capability gate
  // (Supabase: true, Firebase: false); when false the affordance below is
  // never rendered and Beam behaves exactly as it did (LAN-only).
  const { enabled: cloudEnabled, sharing: cloudSharing, shareFiles } = useCloudFileShare();
  const cloudInputRef = useRef(null);

  // LAN beam: a device's "Beam" button (one peer) or "Beam to all" (every
  // nearby peer) opens a native file picker and sends the chosen file(s) over
  // WebRTC. `lanTargetsRef` holds the recipient id(s) for the pending pick —
  // [oneId] for a single device, or all peer ids for "Beam to all".
  const lanInputRef = useRef(null);
  const lanTargetsRef = useRef([]);

  const openCloudPicker = () => {
    if (cloudInputRef.current) cloudInputRef.current.click();
  };

  // Cloud transport is only a valid pick when the provider supports it.
  const useCloud = cloudEnabled && transport === 'cloud';

  const handleBeam = (userId) => {
    setSelectedId(userId);
    if (typeof onSelectPeer === 'function') onSelectPeer(userId);
    if (useCloud) {
      openCloudPicker(); // Cloud is room-wide; the per-device target is moot.
      return;
    }
    lanTargetsRef.current = [userId];
    if (lanInputRef.current) lanInputRef.current.click();
  };

  const handleBeamAll = () => {
    if (useCloud) {
      openCloudPicker();
      return;
    }
    const ids = peers.map((p) => p.userId).filter(Boolean);
    if (ids.length === 0) return;
    lanTargetsRef.current = ids;
    if (lanInputRef.current) lanInputRef.current.click();
  };

  const handleLanInputChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    const targets = lanTargetsRef.current || [];
    if (targets.length === 0 || picked.length === 0) return;
    // Fan out: one independent transfer per (file, peer). The mesh enforces
    // its own concurrency caps; each transfer surfaces in the queue.
    for (const peerId of targets) {
      for (const file of picked) {
        try {
          mesh.startSend(file, peerId);
        } catch {
          /* startSend toasts on the failure modes we care about */
        }
      }
    }
  };

  const handleCloudInputChange = (e) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    // Reset the input so picking the same file twice still fires change.
    e.target.value = '';
    if (picked.length > 0) shareFiles(picked);
  };

  const handleSelect = (userId) => {
    setSelectedId(userId);
    if (typeof onSelectPeer === 'function') onSelectPeer(userId);
  };

  const sameNetCount = peers.filter((p) => p.sameLAN).length;

  // Resolve the selected peer's display handle for the DropZone headline.
  // Falling back to a generic label keeps the UI sane if the peer list churns
  // between selection and drop (e.g. the peer went offline mid-drag).
  const selectedPeer = useMemo(
    () => peers.find((p) => p.userId === selectedId) || null,
    [peers, selectedId],
  );

  // The drop sink. The hook handles the counter pattern, touch fallback, and
  // file-list filtering; we only need to react to the `onDrop` callback.
  const handleDrop = ({ files }) => {
    if (!files || files.length === 0) return;
    // Cloud transport selected → upload to the room (no per-device target).
    if (useCloud) {
      shareFiles(files);
      return;
    }
    if (!selectedId) {
      // LAN mode with no peer chosen. If cloud is available, fall back to it
      // rather than dead-ending; otherwise prompt to pick a peer.
      if (cloudEnabled) {
        shareFiles(files);
        return;
      }
      showToast('Select a peer first');
      return;
    }
    // Iterate; MeshProvider enforces concurrency caps + 2 GB rejections per
    // file. We deliberately don't aggregate the rejections — each failure
    // toasts independently so the user knows which file bounced.
    for (const file of files) {
      try {
        mesh.startSend(file, selectedId);
      } catch {
        /* startSend itself toasts on failure modes we care about */
      }
    }
  };

  const { dragOverProps, active: dragActive } = useFileDrop({
    onDrop: handleDrop,
  });

  const dropVisible = dragActive && selectedId != null;

  return (
    <div className="dbeam" role="region" aria-label="Beam workspace">
      <header className="dbeam__header">
        <span className="dbeam__icon" aria-hidden="true">
          <Icon name="lan" size={18} />
        </span>
        <div className="dbeam__title">
          <span className="dbeam__name">Beam</span>
          <span className="dbeam__sub">
            {peers.length > 0
              ? `LAN · ${peers.length} nearby · ${sameNetCount} on your network`
              : 'LAN · peer-to-peer'}
          </span>
        </div>
        {onClose && (
          <button
            type="button"
            className="dbeam__close"
            onClick={onClose}
            aria-label="Close Beam workspace"
          >
            <Icon name="close" size={16} aria-hidden />
          </button>
        )}
      </header>

      <div className="dbeam__body">
        <section
          className="dbeam__main"
          data-testid="dbeam-main"
          {...dragOverProps}
        >
          <div className="dbeam__toolbar">
            {cloudEnabled ? (
              // Supabase: offer the LAN ↔ Cloud transport toggle (the design's
              // F13.1 segmented control). LAN = direct WebRTC; Cloud = upload to
              // Supabase Storage, shared to the whole room.
              <TransportToggle value={transport} onChange={setTransport} />
            ) : (
              // Firebase: files are LAN-only — no cloud path, so a static badge.
              <span className="t-chip lan" aria-label="LAN-only file transfer">
                <Icon name="wifi" size={11} stroke="#b6ffd6" aria-hidden /> LAN only
              </span>
            )}
            {cloudEnabled && (
              <input
                ref={cloudInputRef}
                type="file"
                multiple
                hidden
                onChange={handleCloudInputChange}
                data-testid="dbeam-cloud-input"
              />
            )}
            {cloudSharing && (
              <span className="dbeam__sharing" role="status" data-testid="dbeam-cloud-spinner">
                <span className="dbeam__cloud-spinner" aria-hidden /> Sharing…
              </span>
            )}
            <span className="dbeam__security">
              <Icon
                name="lan"
                size={13}
                stroke="var(--lan, #3fdc8a)"
                aria-hidden
              />{' '}
              end-to-end encrypted · no server stores files
            </span>
          </div>

          <div className="dbeam__cols">
            <Radar
              peers={peers}
              selfDevice="monitor"
              onSelectPeer={handleSelect}
            />
            <div className="dbeam__device-col">
              <div className="dbeam__device-col-head">
                <div className="dbeam__hint">
                  {scanning
                    ? 'Scanning your network…'
                    : peers.length === 0
                      ? 'No nearby Glacier devices found yet'
                      : 'Tap a device to Beam, or send to everyone'}
                </div>
                {peers.length >= 2 && (
                  <button
                    type="button"
                    className="dbeam__beam-all"
                    onClick={handleBeamAll}
                    aria-label={`Beam a file to all ${peers.length} nearby devices`}
                    data-testid="dbeam-beam-all"
                  >
                    <Icon name="zap" size={12} stroke="#06371f" aria-hidden /> Beam to all ({peers.length})
                  </button>
                )}
              </div>
              {peers.map((peer) => (
                <DeviceRow
                  key={peer.userId}
                  peer={peer}
                  selected={peer.userId === selectedId}
                  onSelect={handleSelect}
                  onBeam={handleBeam}
                />
              ))}
            </div>
          </div>

          {/* Hidden file picker driven by each device's "Beam" button. */}
          <input
            ref={lanInputRef}
            type="file"
            multiple
            hidden
            onChange={handleLanInputChange}
            data-testid="dbeam-lan-input"
          />

          <DropZone
            visible={dropVisible}
            peerName={selectedPeer ? selectedPeer.handle : ''}
          />
        </section>

        <QueueRail />
      </div>
    </div>
  );
};

export default DesktopBeam;
